import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const validationSchema = yup.object({
  password: yup
    .string()
    .min(8, 'Passwort muss mindestens 8 Zeichen lang sein')
    .matches(/[A-Za-z]/, 'Passwort muss mindestens einen Buchstaben enthalten')
    .matches(/\d/, 'Passwort muss mindestens eine Zahl enthalten')
    .required('Passwort ist erforderlich'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwörter müssen übereinstimmen')
    .required('Passwort-Bestätigung ist erforderlich'),
});

const ResetPasswordPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!token) return;

      setLoading(true);
      setError(null);

      try {
        await axios.post(`${API_BASE_URL}/auth/reset-password`, {
          token: token,
          new_password: values.password,
        });
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } catch (err: any) {
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) {
          setError(detail.map((e: any) => e.msg || JSON.stringify(e)).join(', '));
        } else if (typeof detail === 'string') {
          setError(detail);
        } else {
          setError('Zurücksetzen fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
      } finally {
        setLoading(false);
      }
    },
  });

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Ungültiger Reset-Link. Kein Token gefunden.
            </Alert>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Neuen Reset-Link anfordern
            </Link>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Neues Passwort - BTrader</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
              Neues Passwort
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Legen Sie ein neues Passwort für Ihr Konto fest.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Passwort erfolgreich zurückgesetzt! Sie werden zum Login weitergeleitet...
              </Alert>
            ) : (
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Neues Passwort"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  margin="normal"
                  autoComplete="new-password"
                  autoFocus
                />
                <TextField
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Passwort bestätigen"
                  type="password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  margin="normal"
                  autoComplete="new-password"
                />
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, height: 48 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Passwort zurücksetzen'}
                </Button>
              </form>
            )}

            <Box textAlign="center" sx={{ mt: 2 }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Zurück zum Login
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default ResetPasswordPage;
